import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { ConclaveAPI, type Keeper } from '@/api/conclave'

type Status = 'loading' | 'authed' | 'anon'

interface AuthState {
  conclaveUrl: string
  // Assay bug-reporting settings (parked integration; unrelated to the session).
  assayUrl: string
  assayApiKey: string
  setConclaveUrl: (url: string) => void
  setAssayUrl: (url: string) => void
  setAssayApiKey: (key: string) => void

  // Session — the source of truth is the SHARED gate-minted Warden cookie
  // (Domain=.diwata.domains); `keeper` mirrors /auth/me. There is no login
  // action here: unauthenticated visitors are bounced to the gate (App.tsx),
  // which is the only place in the suite that ever asks who you are.
  keeper: Keeper | null
  status: Status
  /** Where the gate lives (relayed from the backend's GATE_PUBLIC_ORIGIN);
   *  null until known. Needed for the silent bounce and for sign-out. */
  gateOrigin: string | null
  /** Conclave's registered gate client id — present ONLY when code-handoff
   *  (CONCLAVE_GATE_SSO) is on and configured. Non-null means RequireAuth does
   *  the code-handoff redirect (client_id + callback); null means it falls back
   *  to the legacy plain-`next` shared-cookie bounce. */
  gateClientId: string | null
  api: () => ConclaveAPI
  loadMe: () => Promise<void>
  /** Fallback local sign-out (clears the shared cookie via the API). The
   *  canonical sign-out navigates to the gate's /logout — see Settings. */
  logout: () => Promise<void>
}

export const useAuth = create<AuthState>()(
  persist(
    (set, get) => ({
      conclaveUrl: import.meta.env.VITE_CONCLAVE_URL ?? 'http://localhost:8002',
      assayUrl: import.meta.env.VITE_ASSAY_URL ?? 'http://localhost:8000',
      assayApiKey: import.meta.env.VITE_ASSAY_API_KEY ?? '',
      setConclaveUrl: (url) => set({ conclaveUrl: url }),
      setAssayUrl: (url) => set({ assayUrl: url }),
      setAssayApiKey: (key) => set({ assayApiKey: key }),

      keeper: null,
      status: 'loading',
      gateOrigin: null,
      gateClientId: null,
      api: () => new ConclaveAPI(get().conclaveUrl),

      loadMe: async () => {
        try {
          const keeper = await get().api().me()
          set({ keeper, status: 'authed' })
        } catch {
          // Not signed in: learn where the gate lives (and whether code-handoff
          // is on) so RequireAuth can bounce there silently. Failure leaves
          // gateOrigin null and the app shows the quiet "gate unreachable" note
          // instead of a form.
          let gateOrigin: string | null = null
          let gateClientId: string | null = null
          try {
            const status = await get().api().gateStatus()
            gateOrigin = status.gate_origin
            gateClientId = status.client_id ?? null
          } catch {
            gateOrigin = null
            gateClientId = null
          }
          set({ keeper: null, status: 'anon', gateOrigin, gateClientId })
        }
      },
      logout: async () => {
        try {
          await get().api().logout()
        } finally {
          set({ keeper: null, status: 'anon' })
        }
      },
    }),
    {
      name: 'conclave-auth',
      // Persist only settings, never the session — the cookie is authoritative.
      partialize: (s) => ({
        conclaveUrl: s.conclaveUrl,
        assayUrl: s.assayUrl,
        assayApiKey: s.assayApiKey,
      }),
      // v2 (2026-08-04): persisted DEV defaults (localhost) from earlier builds
      // silently override the deployed build's env default — every returning
      // browser kept calling a backend that isn't there and the SSO button
      // never appeared. Migrate any persisted localhost URL back to the build
      // default; a genuinely custom URL survives.
      version: 2,
      migrate: (persisted: unknown) => {
        const p = (persisted ?? {}) as Record<string, unknown>
        const build = import.meta.env.VITE_CONCLAVE_URL ?? 'http://localhost:8002'
        const buildAssay = import.meta.env.VITE_ASSAY_URL ?? 'http://localhost:8000'
        if (typeof p.conclaveUrl === 'string' && p.conclaveUrl.includes('localhost')) {
          p.conclaveUrl = build
        }
        if (typeof p.assayUrl === 'string' && p.assayUrl.includes('localhost')) {
          p.assayUrl = buildAssay
        }
        return p
      },
    },
  ),
)
